import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useMiniPlayer } from '../context/MiniPlayerContext';
import { X, ChevronLeft, ChevronRight, Maximize2, Minimize2, Play, Pause, RefreshCw, SkipBack, SkipForward, RotateCcw, RotateCw } from 'lucide-react';

const MiniVideoPlayer = () => {
  const {
    miniPlayerVideo,
    isExpanded,
    videoQueue,
    currentIndex,
    closeMiniPlayer,
    playNext,
    playPrevious,
    expandPlayer,
    minimizePlayer,
  } = useMiniPlayer();

  const iframeRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const videoIdRef = useRef(null);
  
  // Controls visibility state for expanded view
  const [showControls, setShowControls] = useState(true);
  const hideControlsTimeout = useRef(null);
  const containerRef = useRef(null);

  // Track if we need to reload the iframe (only when video ID changes)
  const currentVideoId = miniPlayerVideo?.id;
  const shouldReloadIframe = videoIdRef.current !== currentVideoId;
  
  useEffect(() => {
    if (currentVideoId) {
      videoIdRef.current = currentVideoId;
    }
  }, [currentVideoId]);

  // Auto-hide controls after 3 seconds of no mouse movement in expanded mode
  const resetHideTimer = useCallback(() => {
    setShowControls(true);
    
    if (hideControlsTimeout.current) {
      clearTimeout(hideControlsTimeout.current);
    }
    
    hideControlsTimeout.current = setTimeout(() => {
      if (isExpanded) {
        setShowControls(false);
      }
    }, 3000);
  }, [isExpanded]);

  // Handle mouse movement in expanded view
  const handleMouseMove = useCallback(() => {
    if (isExpanded) {
      resetHideTimer();
    }
  }, [isExpanded, resetHideTimer]);

  // Handle mouse enter - show controls
  const handleMouseEnter = useCallback(() => {
    if (isExpanded) {
      setShowControls(true);
      resetHideTimer();
    }
  }, [isExpanded, resetHideTimer]);

  // Handle mouse leave - hide controls immediately
  const handleMouseLeave = useCallback(() => {
    if (isExpanded) {
      if (hideControlsTimeout.current) {
        clearTimeout(hideControlsTimeout.current);
      }
      setShowControls(false);
    }
  }, [isExpanded]);

  // Show controls when expanded mode changes
  useEffect(() => {
    if (isExpanded) {
      setShowControls(true);
      resetHideTimer();
    }
    
    return () => {
      if (hideControlsTimeout.current) {
        clearTimeout(hideControlsTimeout.current);
      }
    };
  }, [isExpanded, resetHideTimer]);

  // Build YouTube URL - only include start=0 for new videos
  const getYouTubeUrl = useCallback(() => {
    if (!miniPlayerVideo) return '';
    const baseUrl = miniPlayerVideo.embed_url;
    const separator = baseUrl.includes('?') ? '&' : '?';
    // controls=1 ensures YouTube's native scrubber/progress bar is visible
    // rel=0 prevents related videos at the end (helps with auto-play next)
    return `${baseUrl}${separator}enablejsapi=1&autoplay=1&controls=1&rel=0&origin=${window.location.origin}`;
  }, [miniPlayerVideo]);

  // Toggle between mini and expanded view (same iframe, different styling)
  const handleToggleSize = useCallback(() => {
    if (isExpanded) {
      minimizePlayer();
    } else {
      expandPlayer();
    }
  }, [isExpanded, minimizePlayer, expandPlayer]);

  // Send command to YouTube iframe via postMessage
  const sendYouTubeCommand = useCallback((command, args = []) => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      const message = JSON.stringify({
        event: 'command',
        func: command,
        args: args
      });
      iframeRef.current.contentWindow.postMessage(message, '*');
    }
  }, []);

  // Rewind 10 seconds
  const handleRewind = useCallback(() => {
    // Use seekTo command with time offset
    // Since we track current time from YouTube messages, we use that
    const newTime = Math.max(0, currentTime - 10);
    sendYouTubeCommand('seekTo', [newTime, true]);
  }, [currentTime, sendYouTubeCommand]);

  // Fast forward 10 seconds  
  const handleFastForward = useCallback(() => {
    const newTime = currentTime + 10;
    sendYouTubeCommand('seekTo', [newTime, true]);
  }, [currentTime, sendYouTubeCommand]);

  // Toggle play/pause
  const togglePlayPause = useCallback(() => {
    if (isPlaying) {
      sendYouTubeCommand('pauseVideo');
      setIsPlaying(false);
    } else {
      sendYouTubeCommand('playVideo');
      setIsPlaying(true);
    }
  }, [isPlaying, sendYouTubeCommand]);

  // Handle previous with play state
  const handlePrevious = useCallback(() => {
    playPrevious();
    setIsPlaying(true);
  }, [playPrevious]);

  // Handle next with play state
  const handleNext = useCallback(() => {
    playNext();
    setIsPlaying(true);
  }, [playNext]);

  // Handle close
  const handleClose = useCallback(() => {
    closeMiniPlayer();
  }, [closeMiniPlayer]);

  // Suppress errors from YouTube iframe
  useEffect(() => {
    const handleError = (event) => {
      if (event.message === 'Script error.' || event.message?.includes('Script error')) {
        event.preventDefault();
        return true;
      }
    };
    
    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  // Handle YouTube API messages for state changes
  useEffect(() => {
    const handleMessage = (event) => {
      // Accept messages from YouTube
      if (event.origin === 'https://www.youtube.com' || event.origin === 'https://www.youtube-nocookie.com') {
        try {
          const data = JSON.parse(event.data);
          
          // Handle state changes
          if (data.event === 'onStateChange') {
            console.log('🎬 YouTube state change:', data.info);
            if (data.info === 0) {
              // Video ended (state 0), play next
              console.log('🎬 Video ended, playing next...');
              handleNext();
            } else if (data.info === 1) {
              setIsPlaying(true);
            } else if (data.info === 2) {
              setIsPlaying(false);
            }
          }
          
          // Also check for info delivery which contains player state
          if (data.event === 'infoDelivery' && data.info) {
            if (typeof data.info.currentTime === 'number') {
              setCurrentTime(data.info.currentTime);
            }
            // Check playerState in infoDelivery (0 = ended)
            if (data.info.playerState === 0) {
              console.log('🎬 Video ended (via infoDelivery), playing next...');
              handleNext();
            }
          }
        } catch {
          // Not a JSON message - ignore silently
        }
      }
    };

    if (miniPlayerVideo) {
      window.addEventListener('message', handleMessage);
      
      // Subscribe to YouTube player state changes after iframe loads
      const subscribeToEvents = () => {
        if (iframeRef.current && iframeRef.current.contentWindow) {
          // Tell YouTube to send us state change events
          const listenMessage = JSON.stringify({
            event: 'listening',
            id: 1,
            channel: 'widget'
          });
          iframeRef.current.contentWindow.postMessage(listenMessage, '*');
          
          // Also send command to start listening for events
          const commandMessage = JSON.stringify({
            event: 'command',
            func: 'addEventListener',
            args: ['onStateChange']
          });
          iframeRef.current.contentWindow.postMessage(commandMessage, '*');
        }
      };
      
      // Try to subscribe immediately and also after a short delay
      subscribeToEvents();
      const subscribeTimeout = setTimeout(subscribeToEvents, 1000);
      const subscribeTimeout2 = setTimeout(subscribeToEvents, 2000);
      
      // Request info delivery from YouTube player
      const requestTimeUpdate = setInterval(() => {
        sendYouTubeCommand('getCurrentTime');
      }, 1000);
      
      return () => {
        window.removeEventListener('message', handleMessage);
        clearInterval(requestTimeUpdate);
        clearTimeout(subscribeTimeout);
        clearTimeout(subscribeTimeout2);
      };
    }

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [miniPlayerVideo, handleNext, sendYouTubeCommand]);

  // Don't render if no video
  if (!miniPlayerVideo) {
    return null;
  }

  const nextVideo = videoQueue.length > 0 ? videoQueue[(currentIndex + 1) % videoQueue.length] : null;

  // Expanded (full-screen) view
  if (isExpanded) {
    return (
      <div 
        ref={containerRef}
        className={`fixed inset-0 bg-black/95 z-[99999] flex items-center justify-center ${!showControls ? 'cursor-none' : ''}`}
        onClick={handleToggleSize}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Top Control Bar */}
        <div className={`absolute top-4 right-4 z-10 flex items-center gap-2 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
          {/* Minimize Button */}
          <button
            onClick={(e) => { e.stopPropagation(); handleToggleSize(); }}
            className="p-2 bg-black/50 border border-white/20 rounded-full hover:bg-black/70 hover:border-cyan-500/50 hover:shadow-[0_0_15px_rgba(6,182,212,0.3)] transition-all"
            title="Minimize to corner"
          >
            <Minimize2 size={20} className="text-white" />
          </button>
          
          {/* Close Button */}
          <button
            onClick={(e) => { e.stopPropagation(); handleClose(); }}
            className="p-2 bg-black/50 border border-white/20 rounded-full hover:bg-red-500/30 hover:border-red-500/50 transition-colors"
            title="Close"
          >
            <X size={20} className="text-white" />
          </button>
        </div>

        {/* Previous Video Button */}
        <button
          onClick={(e) => { e.stopPropagation(); handlePrevious(); }}
          className={`absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-10 p-3 md:p-4 bg-black/50 border border-white/20 rounded-full hover:bg-black/70 hover:border-white/40 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all group ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          title="Previous video"
        >
          <ChevronLeft size={28} className="text-white group-hover:scale-110 transition-transform" />
        </button>

        {/* Rewind Button */}
        <button
          onClick={(e) => { e.stopPropagation(); handleRewind(); }}
          className={`absolute left-20 md:left-28 top-1/2 -translate-y-1/2 z-10 p-2 md:p-3 bg-black/50 border border-white/20 rounded-full hover:bg-black/70 hover:border-white/40 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all group ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          title="Rewind 10 seconds"
        >
          <RotateCcw size={22} className="text-white group-hover:scale-110 transition-transform" />
        </button>

        {/* Fast Forward Button */}
        <button
          onClick={(e) => { e.stopPropagation(); handleFastForward(); }}
          className={`absolute right-20 md:right-28 top-1/2 -translate-y-1/2 z-10 p-2 md:p-3 bg-black/50 border border-white/20 rounded-full hover:bg-black/70 hover:border-white/40 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all group ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          title="Fast forward 10 seconds"
        >
          <RotateCw size={22} className="text-white group-hover:scale-110 transition-transform" />
        </button>

        {/* Next Video Button */}
        <button
          onClick={(e) => { e.stopPropagation(); handleNext(); }}
          className={`absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-10 p-3 md:p-4 bg-black/50 border border-white/20 rounded-full hover:bg-black/70 hover:border-white/40 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all group ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          title="Next video"
        >
          <ChevronRight size={28} className="text-white group-hover:scale-110 transition-transform" />
        </button>
        
        {/* Video Player - SAME IFRAME, just bigger */}
        <div 
          className="w-full max-w-5xl aspect-video mx-16"
          onClick={(e) => e.stopPropagation()}
        >
          <iframe
            ref={iframeRef}
            key={currentVideoId}
            src={getYouTubeUrl()}
            title={miniPlayerVideo.title}
            className="w-full h-full rounded-lg"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        
        {/* Video Info & Progress - Auto-hide */}
        <div className={`absolute bottom-6 left-0 right-0 px-8 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
          <div className="max-w-5xl mx-auto">
            {/* Track Progress Indicator */}
            <div className="flex items-center justify-center gap-2 mb-3">
              <span className="text-gray-400 text-sm">{currentIndex + 1}</span>
              <span className="text-gray-600">/</span>
              <span className="text-gray-400 text-sm">{videoQueue.length}</span>
            </div>
            
            {/* Current Video Info */}
            <div className="text-center">
              <h2 className="text-xl md:text-2xl font-bold text-white mb-1">
                {miniPlayerVideo.songTitle || miniPlayerVideo.title}
              </h2>
              <p className="text-gray-400">{miniPlayerVideo.artist}</p>
            </div>

            {/* Up Next Preview */}
            {nextVideo && videoQueue.length > 1 && (
              <div className="mt-4 flex items-center justify-center gap-3">
                <span className="text-gray-500 text-xs">Up Next:</span>
                <span className="text-gray-300 text-sm">
                  {nextVideo.songTitle || nextVideo.title}
                </span>
                <span className="text-gray-500 text-xs">by</span>
                <span className="text-gray-400 text-sm">
                  {nextVideo.artist}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Mini (PiP) view
  return (
    <div 
      className="fixed bottom-4 right-4 z-[99998] w-96 bg-black border border-white/20 rounded-xl overflow-hidden shadow-2xl hover:shadow-[0_0_30px_rgba(255,255,255,0.15)] transition-all duration-300 group"
      data-testid="mini-video-player"
    >
      {/* Video Container */}
      <div className="relative aspect-video bg-black">
        <iframe
          ref={iframeRef}
          key={currentVideoId}
          src={getYouTubeUrl()}
          title={miniPlayerVideo.title}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />

        {/* Controls Overlay - Shows on hover, only top bar for expand/close */}
        <div className="absolute top-0 left-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          {/* Top Controls */}
          <div className="absolute top-2 left-2 right-2 flex items-center justify-between z-20">
            <div className="text-white text-xs font-medium bg-black/60 px-2 py-1 rounded">
              {currentIndex + 1} / {videoQueue.length}
            </div>
            <div className="flex items-center gap-1">
              {/* Expand Button */}
              <button
                onClick={(e) => { e.stopPropagation(); handleToggleSize(); }}
                className="p-1.5 bg-black/60 rounded-lg hover:bg-cyan-500/50 transition-colors"
                title="Expand to full screen"
              >
                <Maximize2 size={14} className="text-white" />
              </button>
              
              {/* Close Button */}
              <button
                onClick={(e) => { e.stopPropagation(); handleClose(); }}
                className="p-1.5 bg-black/60 rounded-lg hover:bg-red-500/80 transition-colors"
                title="Close player"
              >
                <X size={14} className="text-white" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Video Info Bar */}
      <div className="p-3 bg-gray-900/95 border-t border-white/10">
        <div className="flex items-center gap-2">
          {/* Quick Play/Pause Button - Always visible */}
          <button
            onClick={togglePlayPause}
            className="flex-shrink-0 w-8 h-8 bg-white/10 border border-white/20 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <Pause size={14} className="text-white" fill="white" />
            ) : (
              <Play size={14} className="text-white ml-0.5" fill="white" />
            )}
          </button>
          
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">
              {miniPlayerVideo.songTitle || miniPlayerVideo.title}
            </p>
            <p className="text-gray-400 text-xs truncate">
              {miniPlayerVideo.artist}
            </p>
          </div>
          
          {/* Navigation & Seek Buttons - Always visible */}
          <div className="flex items-center gap-0.5">
            <button
              onClick={handlePrevious}
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
              title="Previous video"
            >
              <SkipBack size={14} className="text-gray-400 hover:text-white" />
            </button>
            <button
              onClick={handleRewind}
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
              title="Rewind 10s"
            >
              <RotateCcw size={14} className="text-gray-400 hover:text-white" />
            </button>
            <button
              onClick={handleFastForward}
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
              title="Fast forward 10s"
            >
              <RotateCw size={14} className="text-gray-400 hover:text-white" />
            </button>
            <button
              onClick={handleNext}
              className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
              title="Next video"
            >
              <SkipForward size={14} className="text-gray-400 hover:text-white" />
            </button>
          </div>
        </div>

        {/* Up Next */}
        {nextVideo && videoQueue.length > 1 && (
          <div className="mt-2 pt-2 border-t border-white/5 flex items-center gap-2">
            <span className="text-gray-500 text-[10px]">Up next:</span>
            <span className="text-gray-300 text-[10px] truncate flex-1">
              {nextVideo.songTitle || nextVideo.title}
            </span>
            <span className="text-gray-500 text-[10px]">
              {nextVideo.artist}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default MiniVideoPlayer;
